import type { LucideIcon } from "lucide-react";
import {
  EyeOff,
  Bold,
  Italic,
  Strikethrough,
  Code,
  CodeXml,
  Link2,
  Heading1,
  Quote,
} from "lucide-react";
import type { MarkdownFormat } from "@/lib/markdown";

export interface FormattingOption {
  /** Key matching the MarkdownFormat union */
  key: MarkdownFormat;
  /** Whether this format is available in the UI */
  enabled: boolean;
  /** "primary" = always visible; "secondary" = behind expand toggle */
  group: "primary" | "secondary";
  /** Lucide icon component */
  icon: LucideIcon;
  /** i18n key under the "editor" namespace (e.g. "editor.bold") */
  labelKey: string;
}

/**
 * Central config for all formatting options.
 * MarkdownToolbar consumes this list.
 * To disable a format, set `enabled: false`.
 */
const FORMATTING_OPTIONS: FormattingOption[] = [
  {
    key: "spoiler",
    enabled: true,
    group: "primary",
    icon: EyeOff,
    labelKey: "editor.spoiler",
  },
  {
    key: "bold",
    enabled: true,
    group: "primary",
    icon: Bold,
    labelKey: "editor.bold",
  },
  {
    key: "italic",
    enabled: true,
    group: "primary",
    icon: Italic,
    labelKey: "editor.italic",
  },
  {
    key: "strikethrough",
    enabled: true,
    group: "primary",
    icon: Strikethrough,
    labelKey: "editor.strikethrough",
  },
  {
    key: "code",
    enabled: true,
    group: "primary",
    icon: Code,
    labelKey: "editor.code",
  },
  {
    key: "link",
    enabled: true,
    group: "primary",
    icon: Link2,
    labelKey: "editor.link",
  },
  {
    key: "heading",
    enabled: true,
    group: "secondary",
    icon: Heading1,
    labelKey: "editor.heading",
  },
  {
    key: "codeBlock",
    enabled: true,
    group: "secondary",
    icon: CodeXml,
    labelKey: "editor.codeBlock",
  },
  {
    key: "blockquote",
    enabled: true,
    group: "secondary",
    icon: Quote,
    labelKey: "editor.blockquote",
  },
];

const ENABLED_FORMATS = FORMATTING_OPTIONS.filter((o) => o.enabled);

/** Toolbar primary buttons (shown by default in both inline toolbar and popup bubble) */
const TOOLBAR_PRIMARY_KEYS: MarkdownFormat[] = [
  "bold",
  "italic",
  "strikethrough",
  "spoiler",
  "link",
];

/** Toolbar overflow buttons (shown behind "more" toggle) */
const TOOLBAR_OVERFLOW_KEYS: MarkdownFormat[] = [
  "code",
  "codeBlock",
  "heading",
  "blockquote",
];

function resolveFormats(keys: MarkdownFormat[]): FormattingOption[] {
  return keys
    .map((key) => ENABLED_FORMATS.find((o) => o.key === key))
    .filter((o): o is FormattingOption => o != null);
}

export const TOOLBAR_PRIMARY = resolveFormats(TOOLBAR_PRIMARY_KEYS);
export const TOOLBAR_OVERFLOW = resolveFormats(TOOLBAR_OVERFLOW_KEYS);
